import type {ColorScale, DesignSystemDefinition, PillColors} from './types';

type BaseColors = {
  neutral: ColorScale;
  greenColors: ColorScale;
  orangeColors: ColorScale;
};

type SemanticOptions = {
  scorePill?: DesignSystemDefinition['scorePill'];
  alignmentStatus?: DesignSystemDefinition['alignmentStatus'];
  info?: DesignSystemDefinition['semantic']['info'];
};

const defaultScorePill = (orangeColors: ColorScale): DesignSystemDefinition['scorePill'] => ({
  high: {bgcolor: '#e4f3ec', color: '#1c5236', strokeColor: '#1D9E75'},
  medium: {bgcolor: '#fef0d6', color: '#6b3d06', strokeColor: orangeColors[100]},
  low: {bgcolor: '#fce8e8', color: '#7a1f1f', strokeColor: '#E24B4A'},
});

const defaultAlignmentStatus = (): DesignSystemDefinition['alignmentStatus'] => ({
  metAccent: '#1D9E75',
  gapAccent: '#E24B4A',
  gapLabel: '#A32D2D',
  metChipBg: '#E1F5EE',
  metChipFg: '#0F6E56',
  gapChipBg: '#FCEBEB',
  gapChipFg: '#A32D2D',
  warningChipBg: '#fef0d6',
  warningChipFg: '#6b3d06',
});

export const buildSemanticTokens = (
  {neutral, orangeColors}: BaseColors,
  options: SemanticOptions = {},
): Pick<
  DesignSystemDefinition,
  | 'semantic'
  | 'scorePill'
  | 'recommendationCallout'
  | 'dataFreshnessBand'
  | 'redeploymentChip'
  | 'departmentHealthActionType'
  | 'departmentHealthActionPriority'
  | 'profileComparisonSelection'
  | 'alignmentStatus'
> => {
  const scorePill = options.scorePill ?? defaultScorePill(orangeColors);
  const alignmentStatus = options.alignmentStatus ?? defaultAlignmentStatus();
  const info = options.info ?? {bg: '#E6F1FB', fg: '#185FA5'};

  const redeploymentChip = {
    bgcolor: scorePill.low.bgcolor,
    color: scorePill.low.color,
    borderColor: scorePill.low.strokeColor,
  };

  const departmentHealthActionType = {
    skillTraining: {
      iconBg: scorePill.low.bgcolor,
      iconColor: scorePill.low.color,
      iconBorder: scorePill.low.strokeColor,
    },
    singlePointOfFailure: {
      iconBg: scorePill.medium.bgcolor,
      iconColor: scorePill.medium.color,
      iconBorder: scorePill.medium.strokeColor,
    },
    redeploymentReview: {
      iconBg: redeploymentChip.bgcolor,
      iconColor: redeploymentChip.color,
      iconBorder: redeploymentChip.borderColor,
    },
    developmentPlans: {
      iconBg: scorePill.medium.bgcolor,
      iconColor: scorePill.medium.color,
      iconBorder: scorePill.medium.strokeColor,
    },
    dataQuality: {
      iconBg: orangeColors[5],
      iconColor: neutral[600],
      iconBorder: neutral[200],
    },
    allClear: {
      iconBg: scorePill.high.bgcolor,
      iconColor: scorePill.high.color,
      iconBorder: scorePill.high.strokeColor,
    },
  };

  return {
    semantic: {
      success: {bg: scorePill.high.bgcolor, fg: scorePill.high.color},
      warning: {bg: scorePill.medium.bgcolor, fg: scorePill.medium.color},
      error: {bg: scorePill.low.bgcolor, fg: scorePill.low.color},
      info,
    },
    scorePill,
    recommendationCallout: {
      fit: {
        bgcolor: scorePill.high.bgcolor,
        color: scorePill.high.color,
        borderColor: scorePill.high.strokeColor,
      },
      watch: {
        bgcolor: scorePill.medium.bgcolor,
        color: scorePill.medium.color,
        borderColor: scorePill.medium.strokeColor,
      },
      risk: {
        bgcolor: scorePill.low.bgcolor,
        color: scorePill.low.color,
        borderColor: scorePill.low.strokeColor,
      },
    },
    dataFreshnessBand: {
      fresh: {
        bar: scorePill.high.strokeColor,
        bgcolor: scorePill.high.bgcolor,
        color: scorePill.high.color,
        borderColor: scorePill.high.strokeColor,
      },
      ageing: {
        bar: scorePill.medium.strokeColor,
        bgcolor: scorePill.medium.bgcolor,
        color: scorePill.medium.color,
        borderColor: scorePill.medium.strokeColor,
      },
      stale: {
        bar: orangeColors[70],
        bgcolor: orangeColors[5],
        color: orangeColors[950],
        borderColor: orangeColors[100],
      },
      veryStale: {
        bar: scorePill.low.strokeColor,
        bgcolor: scorePill.low.bgcolor,
        color: scorePill.low.color,
        borderColor: scorePill.low.strokeColor,
      },
    },
    redeploymentChip,
    departmentHealthActionType,
    departmentHealthActionPriority: {
      high: {
        bgcolor: scorePill.low.bgcolor,
        color: scorePill.low.color,
        borderColor: scorePill.low.strokeColor,
      },
      medium: {
        bgcolor: scorePill.medium.bgcolor,
        color: scorePill.medium.color,
        borderColor: scorePill.medium.strokeColor,
      },
      low: {
        bgcolor: orangeColors[5],
        color: neutral[600],
        borderColor: neutral[200],
      },
    },
    profileComparisonSelection: {
      slotA: {
        bgcolor: '#e8f4f5',
        color: '#237883',
        borderColor: '#2A8C96',
      },
      slotB: {
        borderColor: scorePill.medium.strokeColor,
        color: scorePill.medium.color,
        bgcolor: scorePill.medium.bgcolor,
      },
    },
    alignmentStatus,
  };
};

export type {PillColors};
